// Supabase client configuration - uses environment variables for security
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { createGuardedFetch, type TripInfo } from './requestGuard';

// Trim to handle any accidental whitespace/newlines in env vars
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL || '').trim();
const SUPABASE_PUBLISHABLE_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim();

// A tripped loop guard is recorded once per request (and at most 5 times per
// page load) so a runaway page shows up in error_logs without itself flooding it.
const MAX_TRIP_REPORTS = 5;
let tripReports = 0;
function reportTrip(info: TripInfo) {
  console.error('[loop-guard] blocked a runaway request', info);
  if (tripReports >= MAX_TRIP_REPORTS) return;
  tripReports += 1;
  void (async () => {
    try {
      const { data } = await supabase.auth.getSession();
      await supabase.from('error_logs').insert({
        severity: 'high',
        category: 'performance_error',
        message: `Loop guard blocked ${info.method} ${info.url.split('?')[0]} (${info.reason}, ${info.count} requests)`.slice(0, 2000),
        user_id: data?.session?.user?.id ?? null,
        user_agent: navigator.userAgent,
        url: window.location.href,
        component: 'supabase-loop-guard',
        action: info.reason,
        metadata: { ...info, url: info.url.slice(0, 2000), pathname: window.location.pathname },
      });
    } catch {
      // Reporting must never become a second failure.
    }
  })();
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  // Every request goes through the loop guard (see requestGuard.ts): a page
  // stuck in a render loop is cut off in the browser before it can exhaust
  // the database, as happened on 2026-10-19.
  global: { fetch: createGuardedFetch((input, init) => fetch(input, init), { onTrip: reportTrip }) },
});

// Untyped client for services accessing tables not yet in auto-generated types.
// Prefer the typed `supabase` export above when possible.
export const supabaseClient = supabase as any;
